import { Ionicons } from '@expo/vector-icons';
import * as MailComposer from 'expo-mail-composer';
import { Redirect, router, useFocusEffect, useNavigation } from 'expo-router';
import { useCallback, useLayoutEffect, useState } from 'react';
import { Linking, Platform, StyleSheet, TouchableOpacity, useWindowDimensions, View } from 'react-native';
import Toast from 'react-native-toast-message';

import { useGetMeMutation } from '@/api/hooks/meHooks';
import { useGetNonProfitInsights } from '@/api/hooks/nonProfitOrgHooks';
import { useGetNpoPaymentAccountSteps } from '@/api/hooks/paymentAccountsHooks';
import BellButton from '@/components/buttons/BellButton';
import BrandedButton from '@/components/buttons/BrandedButton';
import InfoButton from '@/components/buttons/InfoButton';
import NotificationButton from '@/components/buttons/NotificationButton';
import DotsLoader from '@/components/DotsLoader';
import GuardWrapper from '@/components/GuardWrapper';
import TabBarItemDetailsMaterial from '@/components/navigation/TabBarItemDetailsMaterial';
import { TopTabsReanimated } from '@/components/navigation/TopTabsReanimated';
import NpoProfileHeader from '@/components/npo/NpoProfileHeader';
import Text from '@/components/Text';
import { BS_CONTACT_EMAIL } from '@/constants/config';
import { NpoOnboardingRoutes } from '@/constants/enums/NpoOnboardingRoutes';
import { useAuth } from '@/contexts/authContext';
import { useNpoLoginScreenState } from '@/contexts/npoLoginScreenStateContext';
import { i18n } from '@/utils/i18n';
import { npoOnboardingWhereToRoute } from '@/utils/npoOnboardingWhereToRoute';
import GenerateNPOQRButton from '@/components/buttons/GenerateNPOQRButton';

const ProfileScreensLayout = () => {
  const { state: authState, setMeResponse } = useAuth();
  const { height, width } = useWindowDimensions();
  const navigation = useNavigation();
  const { state: npoOnboardingState, setInitialNpoData } = useNpoLoginScreenState();
  const { mutateAsync: getMe, isPending: getMePending } = useGetMeMutation();

  const npoId = authState.signUpData?.loginEmail?.npoId ?? '';

  const { data, isPending, isError, refetch } = useGetNonProfitInsights({
    id: npoId
  });
  const {
    data: paymentAccountSteps,
    isPending: isPaymentAccountStepsPending,
    refetch: refetchSteps
  } = useGetNpoPaymentAccountSteps(npoId);

  const [containerWidth, setContainerWidth] = useState(0);

  const steps = npoOnboardingState.totalPages;
  const completed = npoOnboardingState.currentPage > 0 ? npoOnboardingState.currentPage - 1 : 0;
  const completedPercentage = (completed / steps) * 100;

  //Locales
  const profile_progress = i18n.t('profile.profile_complete_progress', {
    PROGRESS_PERCENT: completedPercentage.toFixed(2)
  });
  const finish_setup = i18n.t('profile.finish_setup');

  const onFinishSetup = async () => {
    await getMe(
      {
        firebaseId: authState.user?.user.firebaseId ?? '',
        onBoardingType: 'npo'
      },
      {
        onSuccess: (meResponse) => {
          setMeResponse(meResponse);
          const type = authState.signUpData?.loginEmail?.type ?? 'CHURCH';
          const preferences =
            type === 'CHARITIES' ? meResponse.onBoardingPreferenceSecular : meResponse.onBoardingPreferenceChurch;

          if (preferences !== null && type && preferences && authState.signUpData?.loginEmail) {
            setInitialNpoData(JSON.stringify(data));
            const whereToRoute = npoOnboardingWhereToRoute(type, preferences);

            router.dismissAll();
            switch (whereToRoute) {
              case NpoOnboardingRoutes.NOTIFICATIONS: {
                router.replace({
                  pathname: whereToRoute,
                  params: { from: 'nonProfit' }
                });
                break;
              }

              case NpoOnboardingRoutes.LOCATION:
                router.replace({
                  pathname: whereToRoute,
                  params: { from: 'nonProfit' }
                });
                break;
              default:
                router.replace({
                  pathname: whereToRoute
                });
                break;
            }
          }
        },
        onError: (e: any) => {
          console.warn('Error getting Me Response', e);
        }
      }
    );
  };

  useLayoutEffect(() => {
    if (npoId) {
      navigation.setOptions({
        headerShown: true,
        headerTransparent: true,
        headerShadow: false,
        headerTitle: () => <></>,
        headerLeft: () => <></>
      });

      if (Platform.OS === 'web') {
        // Web Header Icons
        navigation.setOptions({
          headerRight: () => (
            <View style={[style.headerIcons, { marginRight: 16 }]}>
              <NotificationButton
                hitSlop={10}
                style={style.roundedIcon}
                size={18}
                iconColor="black"
                variant="alternative"
              />

              <BellButton hitSlop={10} style={style.roundedIcon} size={18} iconColor="black" variant="alternative" />

              {data && (
                <TouchableOpacity
                  style={style.roundedIcon}
                  onPress={() =>
                    router.navigate({
                      pathname: '/edit/npoGeneral',
                      params: {
                        npoJson: JSON.stringify(data)
                      }
                    })
                  }>
                  <Ionicons size={18} name="pencil-outline" />
                </TouchableOpacity>
              )}

              <TouchableOpacity
                style={style.roundedIcon}
                onPress={() => {
                  router.navigate('/profile/settings');
                }}>
                <Ionicons size={18} name="settings-sharp" />
              </TouchableOpacity>
            </View>
          )
        });
      } else {
        // Mobile Header Icons
        navigation.setOptions({
          headerRight: () => (
            <View style={style.headerIcons}>
              {data && (
                <TouchableOpacity
                  style={style.roundedIcon}
                  onPress={() =>
                    router.navigate({
                      pathname: '/edit/npoGeneral',
                      params: {
                        npoJson: JSON.stringify(data)
                      }
                    })
                  }>
                  <Ionicons size={24} name="pencil-outline" />
                </TouchableOpacity>
              )}

              <TouchableOpacity
                style={style.roundedIcon}
                onPress={() => {
                  router.navigate('/profile/settings');
                }}>
                <Ionicons size={24} name="settings-sharp" />
              </TouchableOpacity>
            </View>
          ),
          headerLeft: () => (
            <View style={style.headerIcons}>
              <NotificationButton
                hitSlop={10}
                style={style.roundedIcon}
                size={24}
                iconColor="black"
                variant="alternative"
              />
              <BellButton hitSlop={10} style={style.roundedIcon} size={24} iconColor="black" variant="alternative" />
            </View>
          )
        });
      }
    }
  }, [data, navigation, npoId]);

  // FocusEffect to refetch data when we update something
  useFocusEffect(
    useCallback(() => {
      if (npoId) {
        refetch();
        refetchSteps();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [npoId])
  );

  if (authState.user?.user?.role !== 'NON_PROFIT_ORG' && authState.authReady)
    return <Redirect href={'/unauthorized'} />;

  if (isPending || !authState.authReady || isPaymentAccountStepsPending) {
    return (
      <View style={style.loaderContainer}>
        <DotsLoader />
      </View>
    );
  }

  const renderLayout = () => (
    <GuardWrapper canAccess={['NON_PROFIT_ORG']}>
      {!data || isError ? (
        <View style={style.noNpoContainer}>
          <Text>{i18n.t('profile.no_npo_yet')}</Text>
          <BrandedButton style={{ width: '100%' }} onPress={() => router.navigate('/profile/settings')}>
            {i18n.t('profile.go_settings')}
          </BrandedButton>
        </View>
      ) : (
        <View style={style.flexOne}>
          <TopTabsReanimated
            tabBar={(props) => <TabBarItemDetailsMaterial {...props} />}
            headerOptions={
              Platform.OS === 'web'
                ? {
                  maxHeight: width > 768 ? containerWidth / 2 : containerWidth,
                  minHeight: containerWidth / 2
                }
                : {
                  maxHeight: height * 0.55,
                  minHeight: 80 + (!npoOnboardingState.completed ? 35 : 0) + (!data.canReceiveDonations ? 45 : 65)
                }
            }
            header={(animatedOpacityStyle) => (
              <>
                <NpoProfileHeader data={data} animatedOpacityStyle={animatedOpacityStyle} />
                {!data.canReceiveDonations ? (
                  paymentAccountSteps?.result &&
                    paymentAccountSteps.result.allStepsSaved &&
                    paymentAccountSteps.result.hasNuveiAccount &&
                    (paymentAccountSteps.result.docsUploadSaved === null ||
                      paymentAccountSteps.result.docsUploadSaved === undefined ||
                      !paymentAccountSteps.result.docsUploadSaved) ? (
                    // ---- NEEDS DOCUMENT BUTTON ----
                    <View style={style.needDocumentsContainer}>
                      <BrandedButton
                        style={style.needDocumentsButton}
                        onPress={() =>
                          router.navigate({
                            pathname: '/(app)/npoPaymentOnboarding/documents',
                            params: {
                              npoId: data.id
                            }
                          })
                        }>
                        {i18n.t('profile.need_documents')}
                      </BrandedButton>
                      <InfoButton
                        title={i18n.t('profile.need_documents')}
                        message={i18n.t('profile.need_documents_message')}
                        type="modal"
                      />
                    </View>
                  ) : (
                    // ---- RECEIVE DONATION & GENERATE QR BUTTON ----
                    <View style={style.halfButtonsContainer}>
                      <BrandedButton
                        style={style.halfReceiveDonationButton}
                        onPress={() =>
                          router.navigate({
                            pathname: '/(app)/npoPaymentOnboarding/npoPaymentOnboardingIntro',
                            params: {
                              npoId: data.id,
                              npoItem: JSON.stringify(data)
                            }
                          })
                        }>
                        {i18n.t('profile.start_receiving_donations')}
                      </BrandedButton>
                      <GenerateNPOQRButton
                        title={i18n.t('profile.generate_profile_qr')}
                        style={style.halfGenerateProfileQrButton}
                      />
                    </View>
                  )
                ) : paymentAccountSteps?.result &&
                  paymentAccountSteps.result.allStepsSaved &&
                  paymentAccountSteps.result.hasNuveiAccount &&
                  (paymentAccountSteps.result.docsUploadSaved === null ||
                    paymentAccountSteps.result.docsUploadSaved === undefined ||
                    !paymentAccountSteps.result.docsUploadSaved) ? (
                  // ---- NEEDS DOCUMENT BUTTON ----
                  <View style={style.needDocumentsContainer}>
                    <BrandedButton
                      style={style.needDocumentsButton}
                      onPress={() =>
                        router.navigate({
                          pathname: '/(app)/npoPaymentOnboarding/documents',
                          params: {
                            npoId: data.id,
                            npoItem: JSON.stringify(data)
                          }
                        })
                      }>
                      {i18n.t('profile.need_documents')}
                    </BrandedButton>
                    <InfoButton
                      title={i18n.t('profile.need_documents')}
                      message={i18n.t('profile.need_documents_message')}
                      type="modal"
                    />
                  </View>
                ) : (
                  // ---- GENERATE DONATION QR BUTTON ----
                  <View>
                    <GenerateNPOQRButton
                      title={i18n.t('profile.generate_donation_profile_qr')}
                      style={style.generateProfileQrButton}
                    />
                    <View style={style.supportContainer}>
                      <Text>{`${i18n.t('npoPaymentOnboarding.need_help')} `} </Text>
                      <TouchableOpacity
                        onPress={() => {
                          // ToDO: Change for actual support email data
                          if (Platform.OS === 'web') {
                            Linking.openURL(
                              `mailto:${BS_CONTACT_EMAIL}?subject=Support - Broken Shepherds™ # ${authState.user?.user?.fullName}&body=I need assitance with my finnancial account...`
                            );
                          } else {
                            try {
                              MailComposer.composeAsync({
                                recipients: [BS_CONTACT_EMAIL],
                                subject: `Support - Broken Shepherds™ # ${authState.user?.user?.fullName}`,
                                body: `I need assitance with my finnancial account...`
                              });
                            } catch (e) {
                              console.error(e);
                              Toast.show({
                                type: 'error',
                                text1: i18n.t('mix.mail_not_openned.title'),
                                text2: i18n.t('mix.mail_not_openned.message'),
                                position: 'bottom',
                                visibilityTime: 3500
                              });
                            }
                          }
                        }}>
                        <Text style={style.finishSetup} ellipsizeMode="tail" numberOfLines={1}>
                          {i18n.t('mix.contact_us_support')}
                        </Text>
                      </TouchableOpacity>
                    </View>
                  </View>
                )}
                {completed <= steps && !npoOnboardingState.completed && authState.user && (
                  <View style={style.profileProgressContainer}>
                    <View style={style.progressTextSection}>
                      <Text>{profile_progress} </Text>
                      <TouchableOpacity disabled={getMePending} onPress={onFinishSetup}>
                        <Text style={style.finishSetup} ellipsizeMode="tail" numberOfLines={1}>
                          {finish_setup}
                        </Text>
                      </TouchableOpacity>
                    </View>
                  </View>
                )}
              </>
            )}
            screenOptions={{
              swipeEnabled: false
            }}>
            <TopTabsReanimated.Screen
              name="insights"
              initialParams={{
                npoId: data.id
              }}
            />
            <TopTabsReanimated.Screen
              name="programs"
              initialParams={{
                npoId: data.id,
                name: data.name
              }}
            />
            <TopTabsReanimated.Screen
              name="volunteer"
              initialParams={{
                npoId: data.id
              }}
            />
          </TopTabsReanimated>
        </View>
      )}
    </GuardWrapper>
  );

  if (Platform.OS === 'web') {
    return (
      <div
        ref={(node) => {
          if (node) setContainerWidth(node?.clientWidth);
        }}>
        {renderLayout()}
      </div>
    );
  }

  return renderLayout();
};

export default ProfileScreensLayout;

const style = StyleSheet.create({
  flexOne: {
    flex: 1
  },

  noNpoContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 18,
    gap: 15
  },

  roundedIcon: {
    color: 'black',
    backgroundColor: 'white',
    borderRadius: (24 + 16) / 2,
    padding: 8
  },

  profileProgressContainer: {
    width: '100%',
    paddingHorizontal: 18,
    marginTop: 15,
    backgroundColor: 'white'
  },

  progressTextSection: {
    width: '100%',
    flexDirection: 'row',
    justifyContent: 'center'
  },

  finishSetup: {
    color: '#BB822D',
    fontWeight: 'bold',
    flex: 1
  },

  divider: {
    marginTop: 25
  },

  halfButtonsContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    width: '100%',
    paddingHorizontal: 8,
    gap: 5
  },

  halfGenerateProfileQrButton: {
    flex: 1,
    marginTop: 20
  },

  generateProfileQrButton: {
    marginHorizontal: Platform.OS !== 'web' ? 18 : 0,
    marginTop: 20
  },

  halfReceiveDonationButton: {
    flex: 1,
    marginTop: 20
  },

  receiveDonationButton: {
    marginHorizontal: Platform.OS !== 'web' ? 18 : 0,
    marginTop: 20
  },

  halfNeedDocumentsButton: {
    flex: 1
  },

  needDocumentsButton: {
    marginHorizontal: Platform.OS !== 'web' ? 18 : 0,
    marginTop: 20
  },

  headerIcons: {
    flexDirection: 'row',
    gap: 10
  },

  loaderContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center'
  },

  supportContainer: {
    flexDirection: 'row',
    width: '100%',
    paddingHorizontal: 18,
    marginTop: 15,
    backgroundColor: 'white',
    justifyContent: 'center'
  },

  needDocumentsContainer: {
    marginTop: 20,
    paddingHorizontal: 18,
    height: 'auto',
    width: '100%',
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'row',
    gap: 5
  }
});
